// "Trendy" tab: timeline, period comparison and yearly summary.
import { MetricLineChart, VolumeChart } from "./charts";
import { COLORS, GRANULARITY_OPTIONS } from "./constants";
import {
  Card,
  Delta,
  EmptyState,
  RatingCell,
  Segmented,
  SortableTable,
} from "./ui";
import { formatRange, nf } from "./utils";

export const ComparisonTable = ({ kpis, comparison }) => {
  const prev = comparison.kpis;
  const rows = [
    {
      label: "Liczba opinii",
      cur: kpis.total_reviews,
      prev: prev.total_reviews,
      fmt: (v) => nf(v),
      delta: (
        <Delta
          current={kpis.total_reviews}
          previous={prev.total_reviews}
          kind="relative"
        />
      ),
    },
    {
      label: "Średnia ocena",
      cur: kpis.average_rating,
      prev: prev.average_rating,
      fmt: (v) => nf(v, 2),
      delta: prev.total_reviews > 0 && (
        <Delta
          current={kpis.average_rating}
          previous={prev.average_rating}
          digits={2}
        />
      ),
    },
    {
      label: "Opinie pozytywne",
      cur: kpis.positive_share,
      prev: prev.positive_share,
      fmt: (v) => `${nf(v, 1)}%`,
      delta: prev.total_reviews > 0 && (
        <Delta
          current={kpis.positive_share}
          previous={prev.positive_share}
          kind="pp"
        />
      ),
    },
    {
      label: "Opinie negatywne",
      cur: kpis.negative_share,
      prev: prev.negative_share,
      fmt: (v) => `${nf(v, 1)}%`,
      delta: prev.total_reviews > 0 && (
        <Delta
          current={kpis.negative_share}
          previous={prev.negative_share}
          kind="pp"
          goodWhen="down"
        />
      ),
    },
    {
      label: "Sentyment (AI)",
      cur: kpis.average_sentiment_score,
      prev: prev.average_sentiment_score,
      fmt: (v) => `${nf(v, 1)}/10`,
      delta: prev.total_reviews > 0 && (
        <Delta
          current={kpis.average_sentiment_score}
          previous={prev.average_sentiment_score}
        />
      ),
    },
    {
      label: "Poleciłoby firmę",
      cur: kpis.recommendation_rate,
      prev: prev.recommendation_rate,
      fmt: (v) => `${nf(v, 1)}%`,
      delta: prev.recommendation_known_count > 0 && (
        <Delta
          current={kpis.recommendation_rate}
          previous={prev.recommendation_rate}
          kind="pp"
        />
      ),
    },
    {
      label: "Odpowiedzi na negatywne",
      cur: kpis.negative_reply_rate,
      prev: prev.negative_reply_rate,
      fmt: (v) => `${nf(v, 0)}%`,
      delta: prev.negative_reviews > 0 && kpis.negative_reviews > 0 && (
        <Delta
          current={kpis.negative_reply_rate}
          previous={prev.negative_reply_rate}
          kind="pp"
        />
      ),
    },
  ];
  return (
    <div className="gr-table-wrap">
      <table className="gr-table gr-table-compare">
        <thead>
          <tr>
            <th>Wskaźnik</th>
            <th className="is-num">Bieżący</th>
            <th className="is-num">Porównawczy</th>
            <th className="is-num">Zmiana</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td data-label="Wskaźnik" className="is-primary">
                {r.label}
              </td>
              <td data-label="Bieżący" className="is-num">
                <strong>{r.fmt(r.cur)}</strong>
              </td>
              <td data-label="Porównawczy" className="is-num">
                {r.fmt(r.prev)}
              </td>
              <td data-label="Zmiana" className="is-num">
                {r.delta || "–"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TrendsTab = ({ data, granularity, onGranularity, onBucket, onYear }) => {
  const { timeline, yearly, kpis, comparison } = data;
  const yearRows = yearly.map((y, i) => ({ ...y, prev: yearly[i + 1] }));
  return (
    <div className="gr-tab">
      <div className="gr-toolbar">
        <span className="gr-toolbar-label">Agregacja</span>
        <Segmented
          options={GRANULARITY_OPTIONS}
          value={granularity}
          onChange={onGranularity}
          label="Agregacja czasu"
        />
      </div>

      <Card
        id="gr-trend-volume"
        title="Liczba opinii według sentymentu"
        subtitle="Kliknij słupek, aby zobaczyć opinie z danego okresu"
        exportName="trend-liczba-opinii"
      >
        <VolumeChart
          timeline={timeline}
          onSelectBucket={onBucket}
          height={320}
        />
      </Card>

      <div className="gr-grid gr-grid-2">
        <Card
          id="gr-trend-rating"
          title="Średnia ocena Google"
          subtitle="Puste okresy (bez opinii) nie są łączone linią"
          exportName="trend-srednia-ocena"
        >
          <MetricLineChart
            timeline={timeline}
            dataKey="average_rating"
            color={COLORS.accent}
            name="Średnia ocena"
            domain={[(min) => Math.max(1, Math.floor((min - 0.25) * 2) / 2), 5]}
            reference={kpis.total_reviews ? kpis.average_rating : null}
            formatter={(v) => `${nf(v, 2)} ★`}
          />
        </Card>
        <Card
          id="gr-trend-sentiment"
          title="Średni sentyment (AI)"
          subtitle="Skala 1–10 wyliczona przez model dla każdej opinii"
          exportName="trend-sentyment"
        >
          <MetricLineChart
            timeline={timeline}
            dataKey="average_sentiment"
            color="#6250d6"
            name="Sentyment"
            domain={[(min) => Math.max(0, Math.floor(min - 0.5)), 10]}
            reference={kpis.total_reviews ? kpis.average_sentiment_score : null}
            formatter={(v) => `${nf(v, 1)} / 10`}
          />
        </Card>
      </div>

      <div className="gr-grid gr-grid-7-5">
        <Card
          title="Porównanie lat"
          subtitle="Kliknij wiersz, aby przejść do danego roku"
        >
          <SortableTable
            rowKey={(r) => r.year}
            rows={yearRows}
            initialSort={{ key: "year", dir: "desc" }}
            onRowClick={(r) => onYear(r.year)}
            columns={[
              { key: "year", label: "Rok", primary: true },
              {
                key: "total_reviews",
                label: "Opinie",
                align: "right",
                render: (r) => nf(r.total_reviews),
              },
              {
                key: "average_rating",
                label: "Śr. ocena",
                align: "right",
                render: (r) => (
                  <span className="gr-cell-stack">
                    <RatingCell value={r.average_rating} />
                    {r.prev && (
                      <Delta
                        current={r.average_rating}
                        previous={r.prev.average_rating}
                        digits={2}
                      />
                    )}
                  </span>
                ),
              },
              {
                key: "positive_percentage",
                label: "% pozytywnych",
                align: "right",
                render: (r) => `${nf(r.positive_percentage, 1)}%`,
              },
              {
                key: "negative_count",
                label: "Negatywne",
                align: "right",
                render: (r) => nf(r.negative_count),
              },
              {
                key: "recommendation_rate",
                label: "Rekomendacje",
                align: "right",
                render: (r) => `${nf(r.recommendation_rate, 0)}%`,
              },
            ]}
          />
        </Card>
        <Card
          title="Porównanie okresów"
          subtitle={
            comparison
              ? `Okres porównawczy: ${formatRange(comparison.start_date, comparison.end_date)}`
              : undefined
          }
        >
          {comparison ? (
            <ComparisonTable kpis={kpis} comparison={comparison} />
          ) : (
            <EmptyState
              title="Porównanie niedostępne"
              text="Wybierz okres z datą początkową i końcową oraz tryb porównania w filtrach."
            />
          )}
        </Card>
      </div>
    </div>
  );
};

export default TrendsTab;
